import SiteButton from '@/components/atoms/SiteButton'
import andyHeadshot from '@/assets/images/Andy-headshot-2026.png'
import samHeadshot from '@/assets/images/Sam-headshot-2026.png'
import chrisHeadshot from '@/assets/images/chris-headshot-2026.png'
import davidHeadshot from '@/assets/images/david_headshot-2026.png'

// Fixed order per Charly's amendment (David, Samantha, Andy, Chris) — not
// alphabetical, not by seniority. All four buttons read "Book with <name>"
// and now all four have confirmed HubSpot URLs.
const EXPERTS = [
    {
        name: 'David Moore',
        title: 'Chief Technology & Product Officer',
        intro: "I build things. Sensors, data platforms, AI tools, and sometimes stuff that nobody asked for. I co-founded unimo, which started as a sketch on my wall. Come and say hello and I'll talk about it for as long as you'll let me.",
        headshot: davidHeadshot,
        buttonLabel: 'Book with David',
        href: 'https://meetings-eu1.hubspot.com/davidmoore-unimo/ukcw-2026-david',
    },
    {
        name: 'Samantha Botham',
        title: 'Head of Commercial',
        intro: "Every project has that one problem everyone thinks is unsolvable. Bring me yours and let's see if we can fix it. No boring sales pitches, I promise.",
        headshot: samHeadshot,
        buttonLabel: 'Book with Samantha',
        href: 'https://meetings-eu1.hubspot.com/samantha-botham/ukcw-2026',
    },
    {
        name: 'Andy Cross',
        title: 'Chief Operations Officer',
        intro: 'Digital twins, IoT and the AI making sense of the data coming off site. Ask me how any of it actually works, right down to the infrastructure underneath.',
        headshot: andyHeadshot,
        buttonLabel: 'Book with Andy',
        href: 'https://meetings-eu1.hubspot.com/across/ukcw-2026-andy',
    },
    // Chris's source photo frames him lower than the other three, so his
    // headshot alone needs `object-top` to give clear space above his head
    // — the other three are untouched.
    {
        name: 'Chris Kemp',
        title: 'Head of Data & AI Engineering',
        intro: 'Civil engineer by training, data engineer by accident, AI platform builder by choice. I speak fluent site and fluent cloud. Come and find me to see how unimo bridges that gap. Bonus points if you bring me a data problem you think is unsolvable.',
        headshot: chrisHeadshot,
        buttonLabel: 'Book with Chris',
        href: 'https://meetings-eu1.hubspot.com/christopher-kemp/ukcw-2026-chris',
        headshotPosition: 'object-top',
    },
]

// Preview treatment, per Charly: the same top-right-to-Stone gradient as the
// industry-page USP cards (src/features/industry-page/components/UspCard.tsx),
// using the USP token name for its top-left/David's-card hue (turquoise) on
// all four cards here rather than the USP's per-position hue rotation — see
// .claude/rules/unimo-design-system.md.
const CARD_GRADIENT = 'from-unimo-site-turquoise-light'

// Heading: px-8 on mobile (matching the hero's own text inset there), but
// flush left from md: up — Charly's latest call, so on desktop it lines up
// with the card grid's own left edge (which is flush with the hero image's
// edge) rather than the hero's inset headline text. Same md: breakpoint the
// card grid itself already flips at, so the two never fall out of step.
export function ExpertCards() {
    return (
        <section className="pt-section-gap">
            <h2 className="px-8 pb-section-headline-gap-bottom text-2xl font-medium text-unimo-slate md:px-0 lg:text-3xl">
                Book a 15-minute chat with one of our team.
            </h2>
            {/* Outer px-8 is the existing mobile gutter (unchanged, removed at
                md: as before). Inner w-2/3 narrows the card column on mobile
                only, left-aligned within that gutter (not centred — Charly's
                correction) — using Tailwind's fraction scale rather than a
                hand-picked padding number. */}
            <div className="px-8 md:px-0">
                <div className="grid w-2/3 gap-4 md:w-full md:grid-cols-4">
                    {EXPERTS.map((expert) => (
                        <div
                            key={expert.name}
                            className={`flex h-full min-w-0 flex-col gap-3 overflow-hidden rounded-md border-2 border-unimo-site-turquoise bg-gradient-to-bl ${CARD_GRADIENT} to-unimo-stone p-6`}
                        >
                            {/* Headshot: 50% of card width on mobile originally, then +20% to 60%
                                (w-3/5), now +10% again to ~66% (w-2/3, the nearest Tailwind fraction
                                to 60*1.1=66) per Charly — full size again from md up. */}
                            <img
                                src={expert.headshot}
                                alt={expert.name}
                                className={`aspect-square w-2/3 rounded object-cover md:w-full ${expert.headshotPosition ?? ''}`}
                            />
                            {/* Name + title sit as a tight pair with no gap between them — a
                                separate inner block so the card's own gap-3 (which spaces every
                                other direct child) doesn't split them apart. Title is the same
                                family/weight as the name, just smaller, per Charly. */}
                            <div>
                                <p className="text-xl font-medium text-unimo-slate">{expert.name}</p>
                                <p className="text-sm font-medium text-unimo-slate">{expert.title}</p>
                            </div>
                            <p className="font-light text-unimo-slate">{expert.intro}</p>
                            {/* Mobile-only: full width and tighter padding so the button uses the
                                card's full available width. Text size reverted to the default 16px
                                per Charly (was temporarily text-xs to stop "Book with Samantha"/"Book
                                with Andy" wrapping) — at 16px those two labels wrap onto two lines
                                again, flagged back to Charly rather than left silently broken: without
                                `min-w-0` above, the card was quietly overflowing past its own 2/3
                                width to force the text onto one line (a card-sizing bug, not a real
                                fix — same root cause as the StatCard min-width fix elsewhere in this
                                codebase). `min-w-0` keeps the card at its correct width; wrapping
                                (rather than overflow) is the honest consequence of the 16px revert.
                                Reverts to the original w-fit/px-6/py-3 button from md: up. Opens in a
                                new tab per Charly — every outbound link on this page does. */}
                            <SiteButton
                                href={expert.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="mt-auto w-full !px-3 !py-2 md:w-fit md:!px-6 md:!py-3"
                            >
                                {expert.buttonLabel}
                            </SiteButton>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    )
}

export default ExpertCards
